import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchAgentRequests, fetchRequestCount } from "../../api/client";
import { StatusIcon } from "../../lib/status";
import { DateRangePicker } from "../../components/DateRangePicker";
import { getCleanSubject, getOriginalDate } from "../../helpers/emailMetadata";
import {
  ColHeader, useColumnFilterPopover, type ListaColonneCtx,
  PRIORITY_OPTIONS, CAUSALE_OPTIONS,
  prioritaCell, causaleCell, riferimentoCell, impiantoCell, edificioCell, clienteCell,
  confidenzaCell, allegatiCell, getMotivo, hasAudioAttachment,
  formatDataBreve, formatDataEstesa,
} from "../../lib/listaPratiche";
import { AgenteTabs } from "./AgenteTabs";
import type { PaginatedResponse, ProcessingRequest } from "../../api/types";

/* ============================================================
   Dashboard "Agente", scheda "Pratiche": pratiche materializzate da una
   conversazione (WhatsApp/altri canali dell'agente conversazionale). La
   scheda gemella "Conversazioni" (`ConversazioniSessioniList.tsx`,
   `/conversazioni/sessioni`) mostra le conversazioni stesse, comprese
   quelle senza pratica generata.

   Sorgente dati: `/api/v1/requests?origin=conversation` — la STESSA tabella
   e lo STESSO registro di stato di RDI/DDT (`lib/status.tsx`), filtrata sulle
   pratiche con `incoming_data->metadata->>source = 'conversation'` e (sempre,
   lato backend) senza `channel='test'`. Click riga → `/review3/:id`, la
   stessa pagina di dettaglio della coda RDI: e' lo stesso processo di lavoro,
   arricchito da una sezione "Conversazione" (vedi Review3.tsx).

   Queste pratiche si lavorano come le RDI, quindi la lista mostra le stesse
   cose della coda RDI e le mostra allo stesso modo: intestazioni con
   ordinamento e filtro, badge di causale, confidenza colorata, filtro
   temporale. Tutto viene da `lib/listaPratiche`, condiviso con
   `RequestList.tsx` — una seconda tabella scritta a parte divergerebbe alla
   prima modifica.

   Cosa NON e' una colonna, e perche' (verificato sulle pratiche vere, 206
   record a settembre 2026): "Riferimento" c'e' ma e' valorizzato solo sulle
   causali di aggiornamento (14/206) — resta perche' e' esattamente li' che
   serve; "Cliente" viene dal match cliente quando c'e', altrimenti dal
   `codice_cliente` dell'edificio, che e' spesso l'unica traccia.

   `origin=conversation` va passato ANCHE ai conteggi: senza, `/requests/count`
   e' il contatore della coda RDI e le pratiche dell'agente le esclude.

   Pattern tabella copiato da RequestList.tsx/DdtList.tsx: `tableLayout:
   fixed` + `minWidth` NUMERICO letterale sul <table> (mai `var(--space-*)`
   come larghezza — bug di design-token gia' ricomparso piu' volte, coperto
   da tests/test_frontend_design_tokens.py). Le larghezze delle colonne
   comprendono i 24px di padding di `thead th`/`tbody td`: sotto quella
   soglia l'intestazione viene tagliata, perche' `thead th` e' `nowrap` +
   `overflow: hidden`. */

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 50, 100];

export const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Tutti" },
  { value: "pending_review", label: "Da validare" },
  { value: "received", label: "In elaborazione" },
  // Come nella coda RDI: "approvata" e' `done` OPPURE `auto_approved`. Con il
  // solo `done` una pratica approvata automaticamente non compariva da nessuna
  // parte — «dice approvata ma io non la trovo nelle code», ed era vero.
  { value: "done,auto_approved", label: "Approvate" },
  { value: "discarded", label: "Scartate" },
];

const CHANNEL_ICON: Record<string, string> = {
  whatsapp: "chat",
  test: "science",
};

/** Il canale scritto come lo si legge, non come lo scrive il database.
 *  `.badge` forza il maiuscolo — pensato per le parole di stato, non per un
 *  nome proprio: "WHATSAPP" costa 30px in piu' di "WhatsApp" e in una colonna
 *  di tabella quei 30px erano la differenza fra l'etichetta intera e
 *  "WHATSAI". */
const CHANNEL_LABEL: Record<string, string> = {
  whatsapp: "WhatsApp",
  test: "Prova",
};

/** Chi ha scritto: nome se noto, altrimenti telefono/email — mai l'id tecnico. */
function contactLabel(item: ProcessingRequest): string {
  const sender = item.incoming_data?.sender;
  return sender?.name || sender?.phone || sender?.email || "—";
}

/** L'oggetto della pratica. Il subject scritto dal materializer ("Richiesta
 *  via whatsapp — draft 3") non descrive niente: il motivo dell'analisi si',
 *  ed e' valorizzato su 203 pratiche su 206. L'edificio resta come ripiego
 *  per le pratiche fallite prima dell'analisi. */
function oggettoLabel(item: ProcessingRequest): string {
  const motivo = getMotivo(item);
  if (motivo) return motivo;
  const edificio = item.matches?.[0]?.entity_matches?.edificio?.best?.entity;
  const descrizione = edificio?.fields?.descrizione;
  const indirizzo = edificio?.fields?.indirizzo;
  if (descrizione || indirizzo) {
    return [descrizione, indirizzo].filter(Boolean).join(" — ");
  }
  return getCleanSubject(item) || "—";
}

/** Badge del canale. La larghezza della colonna deve contenere l'etichetta
 *  intera: `.badge` e' `max-width: 100%` dentro una cella `overflow: hidden`,
 *  quindi una colonna stretta non manda a capo — taglia, e "WHATSAPP"
 *  diventava "WHATSAI". */
function canaleCell(item: ProcessingRequest) {
  return (
    <span className="badge badge-neutral"
      style={{ fontSize: "var(--font-size-xs)", whiteSpace: "nowrap", maxWidth: "none", textTransform: "none" }}>
      <span className="material-symbols-outlined" style={{ fontSize: "var(--font-size-xs)", verticalAlign: "middle", marginRight: 2 }}>
        {CHANNEL_ICON[item.channel] ?? "forum"}
      </span>
      {CHANNEL_LABEL[item.channel] ?? item.channel}
    </span>
  );
}

export function ConversazioniList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const status = searchParams.get("status") || "";
  const dateFrom = searchParams.get("date_from") || "";
  const dateTo = searchParams.get("date_to") || "";
  const confMin = searchParams.get("conf_min") || "";
  const confMax = searchParams.get("conf_max") || "";
  const fId = searchParams.get("id") || "";
  const fPriority = searchParams.get("priority") || "";
  const fOggetto = searchParams.get("q") || "";
  const fContatto = searchParams.get("sender") || "";
  const fImpianto = searchParams.get("impianto") || "";
  const fCausale = searchParams.get("causale") || "";
  const fNumRif = searchParams.get("numero_riferimento") || "";
  const sort = searchParams.get("sort") || "date_desc";
  const page = parseInt(searchParams.get("page") || "1", 10);
  const pageSize = parseInt(searchParams.get("size") || String(DEFAULT_PAGE_SIZE), 10);

  const [data, setData] = useState<PaginatedResponse<ProcessingRequest> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countPending, setCountPending] = useState<number | null>(null);
  const [countTotal, setCountTotal] = useState<number | null>(null);
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const { openFilter, setOpenFilter } = useColumnFilterPopover();

  function setFilter(updates: Record<string, string | null>) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v === null || v === "" || v === undefined) next.delete(k);
        else next.set(k, v);
      }
      if (!("page" in updates)) next.delete("page");
      return next;
    }, { replace: true });
  }

  function setTextFilter(key: string, value: string) {
    if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);
    debounceTimers.current[key] = setTimeout(() => setFilter({ [key]: value || null }), 300);
  }

  const load = useCallback(() => {
    setIsLoading(true);
    fetchAgentRequests({
      status: status || undefined,
      q: fOggetto || undefined,
      id: fId || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      confidence_min: confMin ? parseFloat(confMin) / 100 : undefined,
      confidence_max: confMax ? parseFloat(confMax) / 100 : undefined,
      priority: fPriority || undefined,
      sender: fContatto || undefined,
      impianto: fImpianto || undefined,
      causale: fCausale || undefined,
      numero_riferimento: fNumRif || undefined,
      sort,
      page,
      size: pageSize,
    })
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e instanceof Error ? e.message : "Errore caricamento"))
      .finally(() => setIsLoading(false));
  }, [status, fOggetto, fId, dateFrom, dateTo, confMin, confMax, fPriority, fContatto, fImpianto, fCausale, fNumRif, sort, page, pageSize]);

  // Contatori: `origin: "conversation"` non e' facoltativo, senza contano le RDI.
  const loadCounts = useCallback(() => {
    const scope = { origin: "conversation" as const, date_from: dateFrom || undefined, date_to: dateTo || undefined };
    fetchRequestCount({ status: "pending_review", ...scope }).then((r) => setCountPending(r.count)).catch(() => {});
    fetchRequestCount({ ...scope }).then((r) => setCountTotal(r.count)).catch(() => {});
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;
  const colCtx: ListaColonneCtx = {
    sort, setFilter, setTextFilter, openFilter, setOpenFilter, confMin, confMax,
  };
  const hasAnyColumnFilter = fOggetto || fContatto || fImpianto || fPriority || fCausale || fNumRif || fId || confMin || confMax;

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--space-3)", gap: "var(--space-3)", rowGap: "var(--space-2)" }}>
        <h1 className="page-title" style={{ flexShrink: 0 }}>Agente</h1>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", flexShrink: 0 }}>
          <span className="text-sm"><span className="text-muted">Da validare: </span>
            <strong style={{ color: "var(--indaco-warning)" }}>{countPending ?? "—"}</strong></span>
          <span style={{ color: "var(--indaco-gray-300)" }}>|</span>
          <span className="text-sm"><span className="text-muted">Totale: </span><strong>{countTotal ?? "—"}</strong></span>
          {hasAnyColumnFilter && (
            <button className="btn btn-ghost btn-sm" title="Pulisci tutti i filtri"
              onClick={() => setFilter({ q: null, sender: null, impianto: null, priority: null, causale: null, numero_riferimento: null, id: null, conf_min: null, conf_max: null })}>
              <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-danger)" }}>filter_list_off</span>
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => { load(); loadCounts(); }} title="Aggiorna">
            <span className="material-symbols-outlined" style={{ fontSize: "var(--font-size-md)" }}>refresh</span>
          </button>
        </div>
      </div>

      <AgenteTabs active="pratiche" />

      <p className="text-sm text-muted" style={{ marginTop: 0, marginBottom: "var(--space-3)" }}>
        Pratiche generate dall'agente conversazionale, lavorate con lo stesso processo delle RDI.
        Click su una riga per aprirla in revisione.
      </p>

      {/* Stato + intervallo di date: stessa barra della coda RDI, stesso
          `DateRangePicker` — non una seconda implementazione. */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--space-3)", flexWrap: "wrap", gap: "var(--space-2)" }}>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", alignItems: "center" }}>
          {STATUS_OPTIONS.map((opt) => (
            <button key={opt.value} className={`btn btn-sm ${status === opt.value ? "btn-primary" : "btn-outline"}`}
              onClick={() => setFilter({ status: opt.value || null })}>
              {opt.label}
            </button>
          ))}
        </div>
        <DateRangePicker from={dateFrom} to={dateTo}
          onChange={(f, t) => setFilter({ date_from: f, date_to: t })} />
      </div>

      {isLoading && !data && <div className="loading-center"><div className="spinner spinner-lg" /></div>}

      {error && (
        <div className="card" style={{ background: "var(--indaco-danger-light)", color: "var(--indaco-danger)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: "var(--font-size-md)", marginRight: "var(--space-1)" }}>error</span> Errore: {error}
        </div>
      )}

      {data && (
        <div className="card" style={{ padding: 0, opacity: isLoading ? 0.6 : 1, transition: "opacity 150ms" }}>
          <div className="table-container">
            <table style={{ tableLayout: "fixed", width: "100%", minWidth: 1388 }}>
              <thead>
                <tr>
                  <ColHeader ctx={colCtx} label="" sortKey="status" style={{ width: 44, textAlign: "center" }} />
                  <ColHeader ctx={colCtx} label="ID" sortKey="id" filterKey="id" filterType="text"
                    filterValue={fId} style={{ width: 80 }} />
                  <ColHeader ctx={colCtx} label="" sortKey="priority" filterKey="priority" filterType="select"
                    filterValue={fPriority} options={PRIORITY_OPTIONS} style={{ width: 66, textAlign: "center" }} />
                  <th style={{ width: 114 }}>Canale</th>
                  <ColHeader ctx={colCtx} label="Contatto" sortKey="sender" filterKey="sender" filterType="text"
                    filterValue={fContatto} style={{ width: 128 }} />
                  <ColHeader ctx={colCtx} label="Oggetto" sortKey="subject" filterKey="q" filterType="text"
                    filterValue={fOggetto} style={{ width: 170 }} />
                  <ColHeader ctx={colCtx} label="Causale" sortKey="causale" filterKey="causale" filterType="select"
                    filterValue={fCausale} options={CAUSALE_OPTIONS} style={{ width: 124, textAlign: "center" }} />
                  <ColHeader ctx={colCtx} label="Rif." sortKey="numero_riferimento" filterKey="numero_riferimento"
                    filterType="text" filterValue={fNumRif} style={{ width: 92 }} />
                  <th style={{ width: 88 }}>Cliente</th>
                  <th style={{ width: 112 }}>Edificio</th>
                  <ColHeader ctx={colCtx} label="Impianto" sortKey="impianto" filterKey="impianto" filterType="text"
                    filterValue={fImpianto} style={{ width: 128 }} />
                  <ColHeader ctx={colCtx} label="Conf." sortKey="confidence" filterKey="confidence" filterType="range"
                    filterValue={confMin || confMax ? `${confMin}-${confMax}` : ""} style={{ width: 102 }} />
                  <ColHeader ctx={colCtx} label="" sortKey="attachments" style={{ width: 44, textAlign: "center" }} />
                  <ColHeader ctx={colCtx} label="Data" sortKey="date" style={{ width: 88 }} />
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 && !isLoading && (
                  <tr>
                    <td colSpan={14} style={{ textAlign: "center", padding: "var(--space-10)", color: "var(--indaco-gray-400)" }}>
                      Nessuna pratica dall'agente conversazionale
                    </td>
                  </tr>
                )}
                {data.items.map((item) => (
                  <tr key={item.id} onClick={() => navigate(`/review3/${item.id}`)} style={{ cursor: "pointer" }}>
                    <td style={{ textAlign: "center", overflow: "hidden" }} title={item.id}>
                      <StatusIcon status={item.status} />
                    </td>
                    <td style={{ overflow: "hidden" }}>
                      <code className="truncate" style={{ display: "block", color: "var(--indaco-gray-600)",
                        fontSize: "var(--font-size-xs)" }} title={item.id}>{item.id.slice(0, 6)}</code>
                    </td>
                    <td style={{ textAlign: "center", overflow: "hidden" }}>{prioritaCell(item)}</td>
                    <td>{canaleCell(item)}</td>
                    <td className="text-sm" style={{ overflow: "hidden" }}>
                      <span className="truncate" style={{ display: "block" }}>{contactLabel(item)}</span>
                    </td>
                    <td style={{ overflow: "hidden" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                        {hasAudioAttachment(item) && (
                          <span className="material-symbols-outlined" title="Messaggio vocale"
                            style={{ fontSize: 14, color: "var(--indaco-brand-primary)", flexShrink: 0 }}>mic</span>
                        )}
                        <span className="truncate" style={{ flex: 1, minWidth: 0, fontSize: "var(--font-size-sm)" }}
                          title={oggettoLabel(item)}>
                          {oggettoLabel(item)}
                        </span>
                      </div>
                    </td>
                    <td style={{ overflow: "hidden", textAlign: "center" }}>{causaleCell(item)}</td>
                    <td style={{ overflow: "hidden" }}>{riferimentoCell(item)}</td>
                    <td style={{ overflow: "hidden" }}>{clienteCell(item)}</td>
                    <td style={{ overflow: "hidden" }}>{edificioCell(item)}</td>
                    <td style={{ overflow: "hidden" }}>{impiantoCell(item)}</td>
                    <td style={{ overflow: "hidden", textAlign: "right" }}>{confidenzaCell(item)}</td>
                    <td style={{ textAlign: "center", overflow: "hidden" }}>{allegatiCell(item)}</td>
                    <td style={{ whiteSpace: "nowrap", overflow: "hidden" }} className="text-sm"
                      title={`Creata: ${formatDataEstesa(getOriginalDate(item))} — ultima attività: ${formatDataEstesa(item.updated_at)}`}>
                      {formatDataBreve(getOriginalDate(item))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && data.total > 0 && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "var(--space-3)", gap: "var(--space-2)" }}>
          {totalPages > 1 && (
            <div className="pagination">
              <button disabled={page <= 1} onClick={() => setFilter({ page: page === 2 ? null : String(page - 1) })}>
                <span className="material-symbols-outlined" style={{ fontSize: "var(--font-size-md)" }}>chevron_left</span>
              </button>
              <span className="text-sm" style={{ padding: "0 var(--space-2)" }}>{page} / {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setFilter({ page: String(page + 1) })}>
                <span className="material-symbols-outlined" style={{ fontSize: "var(--font-size-md)" }}>chevron_right</span>
              </button>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--font-size-sm)" }}>
            <span className="text-muted">Mostra</span>
            <select className="form-input" value={pageSize}
              style={{ width: 72, padding: "var(--space-1) var(--space-2)", fontSize: "var(--font-size-sm)" }}
              onChange={(e) => setFilter({ size: e.target.value === String(DEFAULT_PAGE_SIZE) ? null : e.target.value, page: null })}>
              {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className="text-muted">di {data.total}</span>
          </div>
        </div>
      )}
    </div>
  );
}
